/**
 * Data model for the Overall dashboard's "Gross Revenue (monthly)" chart
 * (dashboard redesign step 4; rework of the former "Monthly Income: BR OS +
 * Portfolio" chart).
 *
 * Every figure is composed from an existing canonical source, never
 * re-derived:
 *   - Portfolio gross revenue/margins: monthlyByRegion.uk/fr from
 *     computeDerivedMetrics() (lib/portfolioDerived.js), the SAME per-month
 *     data the Property Portfolio dashboard's own charts render.
 *   - BR OS turnover/gross margin: the "months" array from
 *     /reports/bros/dashboard (services/bros_revenue.compute_bros_turnover).
 *   - BR OS financing cost: monthly_series from /costs/invoice-financing.
 *   - Portfolio forecast (forward bookings): the "monthly" array from
 *     /reports/forecast (scope=portfolio), the same forward-booking floor
 *     the Property Portfolio dashboard charts use. Only Portfolio gets a
 *     forecast overlay on this chart -- BR OS has no forward-booking source.
 *
 * Segment order: BR OS first (bottom of the stack), Portfolio last (top) --
 * the inverse of the former chart, per the redesign spec. The stacked-bar
 * TOTAL label rides the topmost non-zero segment for that month (_carrier),
 * same pattern as the former chart. Forecast values live on a separate
 * `Portfolio_forecast` key so they are never summed into the actuals total
 * or the actuals Portfolio bar.
 */

import { getActualsForecastBoundary } from './forecastChartUtils';

export var GROSS_REVENUE_SEGMENTS = [
  { key: 'br_os', label: 'BR OS', color: '#3B82F6' },
  { key: 'property_portfolio', label: 'Portfolio', color: '#10B981' },
];

// portfolioDerived's convertDerivedToDisplayCurrency(derived, 'EUR').monthlyByRegion
// renames every _gbp-suffixed field to _eur (deleting the _gbp key) -- unlike
// .totals.combined, which keeps _gbp-suffixed names regardless of target
// currency. buildGrossRevenueChartData below always reads the _gbp-suffixed
// name (matching .totals.combined's convention), so an EUR-converted
// monthlyByRegion MUST be normalized back to _gbp-suffixed keys first, or
// every field silently reads as undefined -> 0 (the actual bug this guards).
var MONTHLY_REGION_CONVERTIBLE_FIELDS = [
  'revenue', 'revenue_actual', 'revenue_forward',
  'gross_revenue', 'gross_revenue_actual', 'gross_revenue_forward',
  'ota_commission', 'management_fees', 'operational',
  'cleaning', 'repairs', 'other_operational', 'financing', 'unclassified', 'total_costs',
  'net_margin', 'net_margin_actual', 'gross_margin', 'gross_margin_actual',
  'avg_nightly_rate', 'revpar',
];

export function normalizeMonthlyByRegionToGbpKeys(monthlyByRegion) {
  var remap = function (entry) {
    var out = Object.assign({}, entry);
    MONTHLY_REGION_CONVERTIBLE_FIELDS.forEach(function (f) {
      out[f + '_gbp'] = entry[f + '_eur'];
    });
    return out;
  };
  return {
    uk: ((monthlyByRegion || {}).uk || []).map(remap),
    fr: ((monthlyByRegion || {}).fr || []).map(remap),
  };
}

/**
 * Stacked TOTAL for a forecast month (Fan, 2026-09-02: "forecasts have no
 * totals"). Actual months carry `_total` and a bold label on their carrier
 * segment; forecast months only had per-segment labels, so the reader had
 * to add the dashed segments by eye. This stamps:
 *   _forecastTotal   = every stacked segment in the bar, actual + forecast
 *                      (a current-month partial is actuals-to-date plus the
 *                      forecast remainder, i.e. the bar's real height)
 *   _forecastCarrier = the `<label>_forecast` key of the TOPMOST forecast
 *                      segment, where the label is drawn (segments stack in
 *                      segmentLabels order, last on top)
 * `_total` is untouched: the actuals invariant (total === BR OS + Portfolio,
 * forecast never merges into it) still holds and is still tested.
 */
export function annotateForecastTotals(rows, segmentLabels) {
  (rows || []).forEach(function (row) {
    if (!row || !row._isForecast) return;
    var total = 0;
    var carrier = null;
    segmentLabels.forEach(function (label) {
      total += row[label] || 0;
      var fc = row[label + '_forecast'];
      if (fc != null) {
        total += fc || 0;
        if (fc) carrier = label + '_forecast';
      }
    });
    row._forecastTotal = total;
    row._forecastCarrier = carrier;
  });
  return rows;
}

function byMonthKey(list) {
  var m = {};
  (list || []).forEach(function (row) { if (row && row.month) m[row.month] = row; });
  return m;
}

/**
 * @param {object} input
 * @param {number} input.year
 * @param {{uk: object[], fr: object[]}} input.ppMonthlyByRegion - computeDerivedMetrics(dataset).monthlyByRegion
 * @param {object[]} input.brosMonths - /reports/bros/dashboard kpis.months (actuals)
 * @param {object[]} input.financingMonthly - /costs/invoice-financing monthly_series
 * @param {object[]} input.ppForecastMonthly - /reports/forecast (scope=portfolio) monthly
 * @param {string[]} input.months - short month labels, e.g. ['Jan', ..., 'Dec']
 * @param {Date} [input.refDate] - injectable "today" for the actuals/forecast boundary (tests)
 */
export function buildGrossRevenueChartData(input) {
  var year = input.year;
  var ppMonthlyByRegion = input.ppMonthlyByRegion || { uk: [], fr: [] };
  var brosByMonth = byMonthKey(input.brosMonths);
  var financingByMonth = byMonthKey(input.financingMonthly);
  var forecastByMonth = byMonthKey(input.ppForecastMonthly);
  var brosForecastByMonth = byMonthKey(input.brosForecastMonths);
  var months = input.months;
  var boundary = getActualsForecastBoundary(input.refDate);

  var rows = [];
  for (var i = 0; i < 12; i++) {
    var mk = year + '-' + String(i + 1).padStart(2, '0');
    var uk = (ppMonthlyByRegion.uk || [])[i] || {};
    var fr = (ppMonthlyByRegion.fr || [])[i] || {};
    // Actuals/forecast split (portfolioDerived.js): meaningful for the
    // current month only (past is fully actual, future is already zero --
    // the dataset query excludes future check-ins). A booking dated in the
    // future is never an actual, regardless of status.
    var hasSplit = uk.gross_revenue_actual_gbp != null || fr.gross_revenue_actual_gbp != null;
    var ppGrossRev = hasSplit
      ? (uk.gross_revenue_actual_gbp || 0) + (fr.gross_revenue_actual_gbp || 0)
      : (uk.gross_revenue_gbp || 0) + (fr.gross_revenue_gbp || 0);
    var ppGrossForward = (uk.gross_revenue_forward_gbp || 0) + (fr.gross_revenue_forward_gbp || 0);
    // Margin actuals/forecast split (dashboards round 2 follow-up): same
    // standing rule as ppGrossRev above -- a same-month forward-dated
    // booking's margin contribution is exactly as un-incurred as its
    // revenue (portfolioDerived.js's net_margin_actual_gbp/
    // gross_margin_actual_gbp). Falls back to the unsplit per-region total
    // only when the backend/fixture hasn't provided the split.
    var ppGrossMarginActual =
      (uk.gross_margin_actual_gbp != null ? uk.gross_margin_actual_gbp : (uk.gross_margin_gbp || 0))
      + (fr.gross_margin_actual_gbp != null ? fr.gross_margin_actual_gbp : (fr.gross_margin_gbp || 0));
    var ppNetMarginActual =
      (uk.net_margin_actual_gbp != null ? uk.net_margin_actual_gbp : (uk.net_margin_gbp || 0))
      + (fr.net_margin_actual_gbp != null ? fr.net_margin_actual_gbp : (fr.net_margin_gbp || 0));

    var bros = brosByMonth[mk] || {};
    var brosTurnover = parseFloat(bros.turnover || 0);
    // A148 tiers: BR OS gross margin is after lease voids, net margin also
    // after invoice financing and overheads (fields merged in from the BR OS
    // P&L by the caller; absent = 0).
    var brosGrossMargin = parseFloat(bros.gross_margin || 0) - parseFloat(bros.lease_voids || 0);
    var financing = financingByMonth[mk] || {};
    var financingCost = parseFloat(financing.total || 0);
    var brosNetMargin = brosGrossMargin - financingCost - parseFloat(bros.overheads || 0);

    var row = {
      month: months[i],
      _monthKey: mk,
      'BR OS': brosTurnover,
      Portfolio: ppGrossRev,
      _grossMargin: ppGrossMarginActual + brosGrossMargin,
      _netMargin: ppNetMarginActual + brosNetMargin,
    };
    row._total = row['BR OS'] + row.Portfolio;
    row._carrier = row.Portfolio > 0 ? 'property_portfolio' : (row['BR OS'] > 0 ? 'br_os' : null);
    // Current month: actuals-to-date (above) plus a forecast-styled
    // remainder for the rest of the month, labelled -- same rule as the
    // entity chart. hasSplit guards against older/mocked data shaped
    // without the split fields (falls back to the pre-existing behaviour).
    if (hasSplit && mk === boundary.currentMonthKey && ppGrossForward > 0) {
      row.Portfolio_forecast = ppGrossForward;
      row._isForecast = true;
    }
    // A margin LINE has no value in a future month; 0 drew a false cliff.
    if (row._monthKey > boundary.currentMonthKey) {
      if ('_grossMargin' in row) row._grossMargin = null;
      if ('_netMargin' in row) row._netMargin = null;
    }
    rows.push(row);
  }

  // Overlay Portfolio forward-booking forecast for the 3 months after the
  // actuals boundary. Never touches BR OS, margins, or the actuals total.
  rows.forEach(function (row) {
    if (row._monthKey < boundary.firstForecastMonthKey || row._monthKey > boundary.lastForecastMonthKey) return;
    var fc = forecastByMonth[row._monthKey];
    // Current month: the Portfolio remainder is the split above.
    if (fc && !row.Portfolio && row._monthKey !== boundary.currentMonthKey) {
      row.Portfolio_forecast = fc.gross_revenue_gbp || 0;
      row._isForecast = true;
    }
    // BR OS forecast (/reports/bros/dashboard forecast_months) never overlaps
    // its actuals: it is the uninvoiced share of live stays plus invoices due
    // later in the window. So it stacks on the current month's actual bar
    // too -- the old "only when the actual is zero" guard is what hid the
    // current month's forecast (2026-10-06).
    var brosFc = brosForecastByMonth[row._monthKey];
    if (brosFc && parseFloat(brosFc.turnover || 0) !== 0) {
      row['BR OS_forecast'] = parseFloat(brosFc.turnover || 0);
      row._isForecast = true;
    }
  });

  return annotateForecastTotals(rows, GROSS_REVENUE_SEGMENTS.map(function (seg) { return seg.label; }));
}

/**
 * Estimated UK management-statement costs (Fan, 2026-10-27).
 *
 * UK managed-property cleaning, repairs and management fees only enter the
 * books when the monthly CWC management statement is imported. Until it
 * lands, the latest month(s) carry those costs at 0 and the Portfolio margin
 * is overstated (October 2026: ~GBP 30k missing). For every month after the
 * last month that has statement costs, up to and including the current
 * month, this returns an ESTIMATE: the trailing 3 statement months' costs as
 * a share of their gross revenue, applied to that month's gross revenue
 * (split actual/forward like revenue). Replaced by the real figure as soon as
 * the statement is imported, because the month then has costs of its own.
 *
 * @returns {Array<{actual:number, forward:number, rate:number}|null>} per month index
 */
export function estimateUkStatementCosts(ukMonthly, currentMonthKey, year) {
  var out = [];
  var stmt = function (m) {
    return (m.management_fees_gbp || 0) + (m.cleaning_gbp || 0) + (m.repairs_gbp || 0);
  };
  var lastIdx = -1;
  for (var i = 0; i < 12; i++) {
    out.push(null);
    var mk = year + '-' + String(i + 1).padStart(2, '0');
    if (mk > currentMonthKey) continue;
    if (stmt((ukMonthly || [])[i] || {}) > 0) lastIdx = i;
  }
  if (lastIdx < 0) return out;
  var costSum = 0;
  var revSum = 0;
  for (var j = Math.max(0, lastIdx - 2); j <= lastIdx; j++) {
    var mj = (ukMonthly || [])[j] || {};
    costSum += stmt(mj);
    revSum += mj.gross_revenue_gbp || 0;
  }
  if (revSum <= 0) return out;
  var rate = costSum / revSum;
  for (var k = lastIdx + 1; k < 12; k++) {
    var mkk = year + '-' + String(k + 1).padStart(2, '0');
    if (mkk > currentMonthKey) break;
    var m = (ukMonthly || [])[k] || {};
    var hasSplit = m.gross_revenue_actual_gbp != null;
    var revActual = hasSplit ? (m.gross_revenue_actual_gbp || 0) : (m.gross_revenue_gbp || 0);
    var revForward = hasSplit ? (m.gross_revenue_forward_gbp || 0) : 0;
    if (revActual + revForward <= 0) continue;
    out[k] = { actual: revActual * rate, forward: revForward * rate, rate: rate };
  }
  return out;
}

/**
 * Data model for the Group dashboard's "Margin" chart (companion to
 * buildGrossRevenueChartData above, now that the Gross Revenue chart no
 * longer carries margin lines). Same BR OS/Portfolio segment split and
 * inputs -- reuses the same per-month actuals math -- just returns Net
 * Margin per segment (for stacked bars) instead of summing it away.
 *
 * Forecast: only the 3-months-ahead window (never a current-month partial),
 * matching the reference Property Portfolio Margin chart exactly --
 * portfolioDerived.js exposes an actuals split for margin but no
 * forward-only component to build a current-month partial bar from.
 *
 * @param {object} input - same shape as buildGrossRevenueChartData's input
 */
export function buildGroupMarginChartData(input) {
  var year = input.year;
  var ppMonthlyByRegion = input.ppMonthlyByRegion || { uk: [], fr: [] };
  var brosByMonth = byMonthKey(input.brosMonths);
  var financingByMonth = byMonthKey(input.financingMonthly);
  var forecastByMonth = byMonthKey(input.ppForecastMonthly);
  var brosForecastByMonth = byMonthKey(input.brosForecastMonths);
  var months = input.months;
  var boundary = getActualsForecastBoundary(input.refDate);

  var ukEstimates = estimateUkStatementCosts(ppMonthlyByRegion.uk, boundary.currentMonthKey, year);

  var rows = [];
  for (var i = 0; i < 12; i++) {
    var mk = year + '-' + String(i + 1).padStart(2, '0');
    var uk = (ppMonthlyByRegion.uk || [])[i] || {};
    var fr = (ppMonthlyByRegion.fr || [])[i] || {};
    var ppGrossMarginActual =
      (uk.gross_margin_actual_gbp != null ? uk.gross_margin_actual_gbp : (uk.gross_margin_gbp || 0))
      + (fr.gross_margin_actual_gbp != null ? fr.gross_margin_actual_gbp : (fr.gross_margin_gbp || 0));
    var ppNetMarginActual =
      (uk.net_margin_actual_gbp != null ? uk.net_margin_actual_gbp : (uk.net_margin_gbp || 0))
      + (fr.net_margin_actual_gbp != null ? fr.net_margin_actual_gbp : (fr.net_margin_gbp || 0));
    var est = ukEstimates[i];
    if (est) {
      ppGrossMarginActual -= est.actual;
      ppNetMarginActual -= est.actual;
    }

    var bros = brosByMonth[mk] || {};
    // A148 tiers: BR OS gross margin is after lease voids, net margin also
    // after invoice financing and overheads (fields merged in from the BR OS
    // P&L by the caller; absent = 0).
    var brosGrossMargin = parseFloat(bros.gross_margin || 0) - parseFloat(bros.lease_voids || 0);
    var financing = financingByMonth[mk] || {};
    var financingCost = parseFloat(financing.total || 0);
    var brosNetMargin = brosGrossMargin - financingCost - parseFloat(bros.overheads || 0);

    var ppNetMarginFull = (uk.net_margin_gbp || 0) + (fr.net_margin_gbp || 0);
    var row = {
      month: months[i],
      _monthKey: mk,
      'BR OS': brosNetMargin,
      Portfolio: ppNetMarginActual,
      _grossMargin: ppGrossMarginActual + brosGrossMargin,
      // Forward = full-month margin minus margin to date (both before the
      // estimate) minus the estimate's forward share.
      _ppNetMarginForward: (uk.net_margin_actual_gbp != null || fr.net_margin_actual_gbp != null)
        ? ppNetMarginFull - (ppNetMarginActual + (est ? est.actual : 0)) - (est ? est.forward : 0) : 0,
      _ppCostEstimate: est ? est.actual + est.forward : null,
    };
    row._total = row['BR OS'] + row.Portfolio;
    // A margin LINE has no value in a future month; 0 drew a false cliff.
    if (row._monthKey > boundary.currentMonthKey) {
      if ('_grossMargin' in row) row._grossMargin = null;
      if ('_netMargin' in row) row._netMargin = null;
    }
    rows.push(row);
  }

  // Overlay the 3-months-ahead margin forecast on BOTH segments: Portfolio
  // from forward bookings, BR OS from contracted nights (added 2026-08-30 --
  // BR OS previously had no forward source at all). Each segment guards
  // independently against stacking a forecast bar on top of its own nonzero
  // actual, so a month that is actual for one segment and forecast for the
  // other still renders correctly.
  rows.forEach(function (row) {
    if (row._monthKey < boundary.firstForecastMonthKey || row._monthKey > boundary.lastForecastMonthKey) return;
    var brosFc = brosForecastByMonth[row._monthKey];
    if (brosFc && parseFloat(brosFc.gross_margin || 0) !== 0) {
      row['BR OS_forecast'] = parseFloat(brosFc.gross_margin || 0);
      row._isForecast = true;
    }
    if (row._monthKey === boundary.currentMonthKey) {
      // Current month: Portfolio remainder = full-month margin - margin to date.
      var fwd = row._ppNetMarginForward || 0;
      if (fwd !== 0) {
        row.Portfolio_forecast = fwd;
        row._isForecast = true;
      }
      return;
    }
    var fc = forecastByMonth[row._monthKey];
    if (!fc || row.Portfolio > 0) return;
    row.Portfolio_forecast = fc.margin_gbp || 0;
    row._isForecast = true;
  });

  return annotateForecastTotals(rows, GROSS_REVENUE_SEGMENTS.map(function (seg) { return seg.label; }));
}

/**
 * YTD (actual months only) per-segment sums plus the combined total, for the
 * figure line under the chart. Forecast-only months contribute zero to both
 * (their actuals fields are 0 by construction).
 */
export function computeGrossRevenueYtd(chartData) {
  var out = { _combined: 0 };
  GROSS_REVENUE_SEGMENTS.forEach(function (seg) { out[seg.label] = 0; });
  (chartData || []).forEach(function (row) {
    GROSS_REVENUE_SEGMENTS.forEach(function (seg) {
      out[seg.label] += row[seg.label] || 0;
    });
    out._combined += row._total || 0;
  });
  return out;
}
